import { gql, useMutation, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { cx } from '@orn/shared-web/utils/cx';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from '../../components/Button';
import Input from '../../components/Input';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';

import { benchmarkFormError, BENCHMARK_POLL_INTERVAL_MS, isTerminalBenchmarkStatus } from './assistantBenchmarkView';
import BenchmarkRunView, { CommitLink, StatusBadge } from './BenchmarkRunView';

import type {
  AdminGetAssistantBenchmarkQuery,
  AdminGetAssistantBenchmarkQueryVariables,
  AdminListAssistantBenchmarksQuery,
  AdminStartAssistantBenchmarkMutation,
  AdminStartAssistantBenchmarkMutationVariables,
} from './__generated__/AssistantBenchmarksScene.gql';

const ADMIN_LIST_BENCHMARKS = gql`
  query AdminListAssistantBenchmarks {
    adminListAssistantBenchmarks {
      id
      baseSha
      headSha
      scenarioGlob
      status
      githubRunUrl
      createdAt
      completedAt
    }
  }
`;

const ADMIN_GET_BENCHMARK = gql`
  query AdminGetAssistantBenchmark($id: ID!) {
    adminGetAssistantBenchmark(id: $id) {
      id
      baseSha
      headSha
      scenarioGlob
      status
      githubRunUrl
      verdict
      createdAt
      completedAt
    }
  }
`;

const ADMIN_START_BENCHMARK = gql`
  mutation AdminStartAssistantBenchmark($input: AdminStartAssistantBenchmarkInput!) {
    adminStartAssistantBenchmark(input: $input) {
      id
      status
    }
  }
`;

/**
 * Benchmarks sub-tab of the Assistant tool (staging-only). Dispatches an
 * on-demand two-commit assistant benchmark (base vs head) to GitHub Actions,
 * lists recent runs, and — via the `?runId=` detail pane — polls one run until
 * it settles and renders its per-scenario verdict. Reconciliation against GitHub
 * happens server-side on the detail poll, so opening a run advances its status.
 */
const AssistantBenchmarksScene = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRunId = searchParams.get('runId');

  const [baseSha, setBaseSha] = React.useState('');
  const [headSha, setHeadSha] = React.useState('');
  const [scenarioGlob, setScenarioGlob] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { data: listData, refetch: refetchList } = useQuery<AdminListAssistantBenchmarksQuery>(ADMIN_LIST_BENCHMARKS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });
  const runs = listData?.adminListAssistantBenchmarks ?? [];

  const [startBenchmark, { loading: starting }] = useMutation<
    AdminStartAssistantBenchmarkMutation,
    AdminStartAssistantBenchmarkMutationVariables
  >(ADMIN_START_BENCHMARK);

  const formError = benchmarkFormError(baseSha, headSha);

  const handleStart = async () => {
    setConfirmOpen(false);
    try {
      const result = await startBenchmark({
        variables: {
          input: {
            baseSha: baseSha.trim(),
            headSha: headSha.trim(),
            scenarioGlob: scenarioGlob.trim() === '' ? null : scenarioGlob.trim(),
          },
        },
      });
      const id = result.data?.adminStartAssistantBenchmark.id;
      if (id) {
        showToast({ type: 'success', message: 'Benchmark dispatched — polling for the verdict.' });
        setSearchParams({ runId: id });
        await refetchList();
      }
    } catch (error) {
      // The mutation's preflight rejects (old head, too many in flight) come back
      // as GraphQL errors — surface the reason so the operator can correct it.
      showToast({ type: 'error', message: error instanceof Error ? error.message : 'Failed to start the benchmark.' });
    }
  };

  const openRun = (id: string) => setSearchParams({ runId: id });

  // Stable identity so BenchmarkDetail's settle effect doesn't re-fire on every
  // parent render (a settle triggers refetchList → re-render → new callback).
  const handleSettled = React.useCallback(() => {
    void refetchList();
  }, [refetchList]);

  return (
    // Owns its own scrolling: the Assistant shell is pinned to the viewport.
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      {/* Start form */}
      <div className="border-border-primary flex flex-col gap-2 border-b pb-4">
        <div className="flex items-end gap-3">
          <Input
            className="w-64"
            label="Base commit (baseline)"
            placeholder="prior-release SHA or ref"
            value={baseSha}
            onChange={setBaseSha}
          />
          <Input
            className="w-64"
            label="Head commit (candidate)"
            placeholder="candidate SHA or ref"
            value={headSha}
            onChange={setHeadSha}
          />
          <Input
            className="w-56"
            label="Scenario filter (optional)"
            placeholder="BOOT-*  ·  blank = whole suite"
            value={scenarioGlob}
            onChange={setScenarioGlob}
          />
          <Button
            color="primary"
            size="sm"
            isDisabled={formError !== null || starting}
            isLoading={starting}
            onClick={() => setConfirmOpen(true)}
          >
            Run benchmark
          </Button>
        </div>
        {/* Form-level validation — shown only once the operator starts typing, so
            an untouched form isn't scolded; the button stays disabled regardless. */}
        {formError !== null && (baseSha.trim() !== '' || headSha.trim() !== '') && (
          <span className="text-fg-error-primary text-xs">{formError}</span>
        )}
      </div>

      <div className="flex items-start gap-6">
        {/* Recent runs */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">
            Recent runs{runs.length > 0 && ` · ${runs.length}`}
          </h2>
          {runs.length === 0 ? (
            <span className="text-fg-tertiary text-xs italic">No runs yet — dispatch one above.</span>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>When</Table.HeaderCell>
                  <Table.HeaderCell>Base → Head</Table.HeaderCell>
                  <Table.HeaderCell>Filter</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {runs.map((run) => (
                  <Table.Row
                    key={run.id}
                    onClick={() => openRun(run.id)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Open benchmark ${run.id}`}
                    onKeyDown={(e) => {
                      // Ignore keydowns bubbling from nested links (the commit SHAs)
                      // so Enter on a focused SHA opens GitHub, not also the row.
                      if (e.target !== e.currentTarget) {
                        return;
                      }
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openRun(run.id);
                      }
                    }}
                    className={cx(
                      'cursor-pointer focus-visible:outline focus-visible:outline-2',
                      run.id === selectedRunId && 'bg-bg-brand-secondary'
                    )}
                  >
                    <Table.Cell mono muted className="whitespace-nowrap" title={new Date(run.createdAt).toISOString()}>
                      {formatTimeAgo(run.createdAt)}
                    </Table.Cell>
                    <Table.Cell mono className="whitespace-nowrap">
                      <CommitLink sha={run.baseSha} /> → <CommitLink sha={run.headSha} />
                    </Table.Cell>
                    <Table.Cell mono muted>
                      {run.scenarioGlob ?? '—'}
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={run.status} />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>

        {/* Detail pane */}
        {selectedRunId !== null && (
          <BenchmarkDetail
            key={selectedRunId}
            runId={selectedRunId}
            onClose={() => setSearchParams({})}
            onSettled={handleSettled}
          />
        )}
      </div>

      {/* Cost confirm */}
      <Modal isOpen={confirmOpen} onOpenChange={setConfirmOpen} size="md">
        <Modal.Header onClose={() => setConfirmOpen(false)}>Run assistant benchmark?</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <p className="text-fg-secondary text-sm">
              This runs the full escalating-ladder benchmark comparing <CommitLink sha={headSha.trim()} /> against{' '}
              <CommitLink sha={baseSha.trim()} />
              {scenarioGlob.trim() !== '' && (
                <>
                  {' '}
                  on <span className="text-fg-primary font-mono">{scenarioGlob.trim()}</span>
                </>
              )}
              . It's a real-model eval — expect tens of minutes of runtime and meaningful API spend, so dispatch
              deliberately.
            </p>
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" size="sm" onClick={() => void handleStart()}>
                Dispatch run
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

interface IBenchmarkDetailProps {
  runId: string;
  onClose: () => void;
  onSettled: () => void;
}

/**
 * Detail pane for one run. Polls `adminGetAssistantBenchmark` (which reconciles the run
 * against GitHub) every few seconds until it settles, then renders the verdict.
 */
const BenchmarkDetail = ({ runId, onClose, onSettled }: IBenchmarkDetailProps) => {
  const { data, error, stopPolling } = useQuery<
    AdminGetAssistantBenchmarkQuery,
    AdminGetAssistantBenchmarkQueryVariables
  >(ADMIN_GET_BENCHMARK, {
    variables: { id: runId },
    pollInterval: BENCHMARK_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: false,
  });
  const run = data?.adminGetAssistantBenchmark ?? null;
  const status = run?.status ?? null;

  // Latch so settlement notifies the parent exactly once even if the effect
  // re-runs; `key={runId}` on this component resets it per run.
  const hasNotifiedSettledRef = React.useRef(false);
  React.useEffect(() => {
    if (status !== null && isTerminalBenchmarkStatus(status) && !hasNotifiedSettledRef.current) {
      hasNotifiedSettledRef.current = true;
      stopPolling();
      onSettled();
    }
  }, [status, stopPolling, onSettled]);

  return (
    <div className="border-border-primary flex w-[36rem] shrink-0 flex-col border p-4">
      <BenchmarkRunView
        run={run}
        hasQueryError={Boolean(error)}
        pollIntervalMs={BENCHMARK_POLL_INTERVAL_MS}
        onClose={onClose}
      />
    </div>
  );
};

export default AssistantBenchmarksScene;
